/**
 * The PROMOTED discount code — a code the shopper never typed, carried in the link she clicked.
 *
 * A seller who runs a campaign does not want to tell people "and remember to type WELCOME10 at the
 * checkout": every shopper who forgets is a shopper who pays full price and then writes to ask why. She
 * wants ONE address to put in the post, and the price already cut when they arrive. That address is what
 * this file remembers between the click and the money.
 *
 * It lives in the shared package because FIVE call sites need the same answer, in two different apps: the
 * storefront's `/discount/<CODE>` redirect, the boot script that catches `?discount=` on any page, the
 * /cart footer's own field, the classic checkout form, and the one-page COD buy box (which ships from
 * here). A second copy of the storage key would mean a code saved by one surface and invisible to the next.
 *
 * Three rules shaped it:
 *
 *  1. **A COOKIE, not localStorage.** The `/discount/<CODE>` route answers with a 302 and no HTML, so there
 *     is no page there to run a script in — a server-set cookie is the only thing that can carry the code
 *     across that redirect without a visible flash of a blank page. /checkout is server-rendered too and
 *     reads the same jar.
 *  2. **It never throws and never blocks a sale.** Everything is guarded. A browser with cookies refused
 *     degrades to "no promoted code", which is the honest answer: the shopper then types it, as before.
 *  3. **It is a DISPLAY intent, never a price.** The stored value is a code and nothing else — no amount, no
 *     percentage. Every surface that reads it asks the backend to quote it against the real cart
 *     (`/api/discount-preview`), and the order re-prices it a third time on placement. A hand-edited cookie
 *     can therefore change which code is TRIED, which is already public information — the link is meant to
 *     be shared — and never what the buyer is charged.
 *
 * No store scope in the key, deliberately. In production one shop is one origin (its subdomain or its own
 * domain), so the jar is already the shop's. On `astro dev`, where every shop answers on one
 * `localhost:4321` and is picked with `?store=`, a code saved on one shop does travel to the next — and
 * that is harmless HERE in a way it was not for the cart: nothing is priced in the browser, so a foreign
 * code is simply quoted, refused by the backend, and forgotten by {@link clearPromoCode}. The cart had to
 * scope its key because a foreign LINE poisoned the whole total (22/09/2026); a foreign code cannot.
 */
/** The jar entry. Read by the islands, so it is deliberately not HttpOnly. */
export const PROMO_COOKIE = 'aqx_promo';
/** The query parameter a seller can append to ANY storefront address (`/products/x?discount=WELCOME10`). */
export const PROMO_QUERY_PARAM = 'discount';
/** 30 days. Long enough for a campaign, short enough that a code does not haunt a browser for a year. */
const MAX_AGE_SECONDS = 30 * 24 * 60 * 60;
/**
 * The backend's own code rule, mirrored: `DiscountCode.of` normalises to upper case and accepts 3-64 chars
 * of `A-Z 0-9 . _ -`. Mirrored rather than asked over the network because this runs before any fetch — on
 * the redirect route, and in a boot script that must not add a round trip to every page view. A code that
 * fails here would fail there too, so refusing it now costs nothing and keeps junk out of the jar.
 *
 * Returns the normalised code, or `null` for anything that is not one.
 */
export function normalizePromoCode(raw) {
    if (typeof raw !== 'string')
        return null;
    const normalized = raw.trim().toUpperCase();
    return /^[A-Z0-9._-]{3,64}$/.test(normalized) ? normalized : null;
}
/** The `Set-Cookie` value for a promoted code — used by the SSR `/discount/<CODE>` route. */
export function promoCookieHeader(code) {
    return `${PROMO_COOKIE}=${code}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax`;
}
/** Read the promoted code out of a raw `Cookie:` header (SSR side). `null` when there is none. */
export function promoCodeFromCookieHeader(header) {
    if (!header)
        return null;
    for (const part of header.split(';')) {
        const eq = part.indexOf('=');
        if (eq === -1)
            continue;
        if (part.slice(0, eq).trim() !== PROMO_COOKIE)
            continue;
        return normalizePromoCode(decodeURIComponent(part.slice(eq + 1).trim()));
    }
    return null;
}
/**
 * The promoted code this browser is carrying, or `null`. Safe to call during SSR (answers `null`).
 *
 * It reads the jar FIRST and the address SECOND, and the second half is not a convenience: the boot script
 * that catches `?discount=` and the React islands that read the code are both deferred modules, so there
 * is no guarantee the script has run by the time an island hydrates. A cookie-only read would therefore
 * apply a `?discount=` link on the SECOND page view and not on the one the shopper actually clicked —
 * exactly the half-working behaviour a promotional link cannot have. Reading the address too makes the
 * order irrelevant, and saves the code so the rest of the visit still finds it.
 */
export function readPromoCode() {
    if (typeof document === 'undefined')
        return null;
    try {
        const fromJar = promoCodeFromCookieHeader(document.cookie);
        if (fromJar)
            return fromJar;
    }
    catch {
        // A jar we may not read falls through to the address below.
    }
    try {
        const raw = new URL(window.location.href).searchParams.get(PROMO_QUERY_PARAM);
        if (raw === null)
            return null;
        const code = normalizePromoCode(raw);
        // Save it on the way past: this island may be gone (a navigation, an unmount) before the boot script
        // gets there, and the code has to outlive it.
        if (code)
            savePromoCode(code);
        return code;
    }
    catch {
        // No window, an unparseable address: the shopper has no promoted code and can still type one.
        return null;
    }
}
/** Remember a promoted code in this browser. Junk is dropped rather than stored. Returns what was kept. */
export function savePromoCode(raw) {
    const code = normalizePromoCode(raw);
    if (!code || typeof document === 'undefined')
        return null;
    try {
        document.cookie = promoCookieHeader(code);
    }
    catch {
        return null;
    }
    return code;
}
/**
 * Forget the promoted code.
 *
 * Called the moment the backend refuses it — an expired campaign, a code for another shop, a usage cap
 * reached. Without this, every page of the visit would re-ask the backend about a code that will never
 * work again, and the /cart footer would keep flashing a discount row that cannot be honoured.
 */
export function clearPromoCode() {
    if (typeof document === 'undefined')
        return;
    try {
        document.cookie = `${PROMO_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
    }
    catch {
        // Nothing to do: a jar we cannot write is a jar whose entry will expire on its own.
    }
    // And out of the ADDRESS, or forgetting it would not stick: {@link readPromoCode} falls back to
    // `?discount=`, so on the very page the link landed on the next reader would find the refused code again
    // and re-ask the backend about it on every render.
    stripPromoParam();
}
/** Take `?discount=` out of the current address, leaving the rest of the URL alone. */
function stripPromoParam() {
    try {
        const win = window;
        const url = new URL(win.location.href);
        if (url.searchParams.get(PROMO_QUERY_PARAM) === null)
            return;
        url.searchParams.delete(PROMO_QUERY_PARAM);
        // `replaceState`, never `assign`: the shopper clicked ONE link and must not find a redirect of it
        // sitting in her history, where Back would re-apply the code she just removed.
        win.history.replaceState(win.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    }
    catch {
        // A locked-down history API costs a tidy address, never the page.
    }
}
/**
 * Catch `?discount=<CODE>` on ANY storefront address, save it, and take it back out of the URL.
 *
 * The dedicated `/discount/<CODE>` route is the clean shareable address, but it always lands on the home
 * page (or wherever `?redirect=` says). What a seller posting about ONE product actually wants is that
 * product's own link with the code riding along — so this accepts the parameter everywhere.
 *
 * The parameter is stripped with `replaceState` for three reasons, none cosmetic: the address the shopper
 * copies and re-shares stays clean, a reload does not re-save a code she may have since removed, and the
 * canonical/analytics view of the page is not forked into one URL per campaign.
 */
export function initPromoLink(win = window) {
    try {
        const raw = new URL(win.location.href).searchParams.get(PROMO_QUERY_PARAM);
        if (raw === null)
            return;
        const code = savePromoCode(raw);
        // Only once it IS in the jar. Stripping an unsaveable code (cookies refused) would lose it outright,
        // where leaving it in the address at least lets the islands on this page read it.
        if (code)
            stripPromoParam();
    }
    catch {
        // A locked-down history API or an unparseable address costs the promoted code, never the page.
    }
}
//# sourceMappingURL=promoLink.js.map